/**
 * 财务健康三层（**K 域 K4**）—— 「扛不扛得住 / 钱留不留得住 / 钱摆得对不对」
 *
 * 依据：K 域 PRD **§4.5.4** · `AC-K4.10`（三层组织，**不平铺 7 个数字**）· `AC-K4.11`（每层一句陈述）·
 * `AC-K4.12`（无健康分 / 无评分 / 无排名）· `AC-K4.13`（数据不足 ≠ 0）· `AC-K4.14`（口径可展开）·
 * `AC-K4.15`（每层 = 陈述 + 一个动作）· `AC-K4.16`（无数字支撑的动作不显示）。
 *
 * ## 🔴 本组件一个数都不算
 *
 * | 显示 | 来源 |
 * |---|---|
 * | 三层 + 每层的指标 | 服务端 `lines`（**端侧不补空层**：没有就是没有） |
 * | 数值文本 | 服务端 `valueE4` → `metricText()`（只排版） |
 * | 三态（正常 / 数据不足 / 未就绪） | 服务端 `sufficient` / `unavailable` |
 * | 动作句 | 服务端 `advice`（`kind` + 数字）→ `adviceText()`（只拼词） |
 *
 * ⚠️ **不画雷达图 / 不画健康分 / 不排名**（`AC-K4.12`）—— 三层就是三张并排的卡。
 */

import { Alert, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import type { HealthLineView } from '@jiaying/shared';
import { healthLineModels, type MetricRow } from '../features/report.js';
import { INSUFFICIENT_TEXT, UNAVAILABLE_PREFIX } from '../features/report-labels.js';

/** 三态的**颜色语义**：正常中性（🔴 健康不是"涨"）、数据不足灰、未就绪灰 —— 都不用红色审判 */
function MetricRowView({ row }: { row: MetricRow }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      spacing={1}
      sx={{ py: 0.25 }}
      data-testid={`health-metric-${row.key}`}
    >
      <Typography variant="caption" color="text.secondary">
        {row.label}
      </Typography>
      {row.state === 'ok' ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid={`health-value-${row.key}`}>
          {row.text}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" data-testid={`health-value-${row.key}`}>
          {row.state === 'insufficient' ? INSUFFICIENT_TEXT : `${UNAVAILABLE_PREFIX}${String(row.note ?? '')}`}
        </Typography>
      )}
    </Stack>
  );
}

export function ReportHealthLines({
  lines,
  currency,
}: {
  lines: readonly HealthLineView[];
  currency: string;
}) {
  const models = healthLineModels(lines, currency);

  if (models.length === 0) {
    // `AC-K4.17`：整层数据不足 ⇒ 服务端不给层 ⇒ 端侧给**一句解释**，不画空卡、不显示 0
    return (
      <Alert severity="info" data-testid="health-empty">
        数据还不够，先记几笔账再看这一块。
      </Alert>
    );
  }

  return (
    <Box data-testid="report-health">
      {models.map((m) => (
        <Card key={m.layer} sx={{ mb: 1.5 }} data-testid={`health-line-${m.layer}`}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {m.question}
              </Typography>
              <Chip size="small" variant="outlined" label={m.layer} />
            </Stack>

            {/* 陈述：该层各指标（🔴 数值全是服务端算的） */}
            <Box sx={{ mt: 1 }} data-testid={`health-rows-${m.layer}`}>
              {m.rows.map((row) => (
                <MetricRowView key={row.key} row={row} />
              ))}
            </Box>

            {/* 动作：🔴 只在服务端给了动作时才出现（`AC-K4.16`：泛泛建议不显示） */}
            {m.advice === null ? null : (
              <Typography
                variant="body2"
                sx={{ mt: 1 }}
                color="text.primary"
                data-testid={`health-advice-${m.layer}`}
              >
                {m.advice}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
